import { AppliedFilters, Table } from "@fluidattacks/design";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useOrgNetworkLogsFilters } from "./filters";
import { useNetworkLogsQuery } from "./hooks";
import type { IZtnaNetworkLogData } from "./types";

import { Buttons } from "../action-buttons";
import type { IBackFilters, IZtnaLogsProps } from "../types";
import { Can } from "context/authz/can";
import { ZtnaLogType } from "gql/graphql";
import { useTable } from "hooks/use-table";

const OrganizationZtnaNetworkLogs: FC<IZtnaLogsProps> = ({
  organizationId,
}): JSX.Element => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [backFilters, setBackFilters] = useState<IBackFilters>({
    endDate: undefined,
    startDate: "",
  });
  const [filteredDataset, setFilteredDataset] = useState<IZtnaNetworkLogData[]>(
    [],
  );

  const tableHeaders: ColumnDef<IZtnaNetworkLogData>[] = [
    {
      accessorKey: "date",
      header: t("organization.tabs.ztna.tabs.network.date"),
      size: 500,
    },
    {
      accessorKey: "email",
      header: t("organization.tabs.ztna.tabs.network.email"),
    },
  ];

  const { networkLogs, loading } = useNetworkLogsQuery({
    backFilters,
    organizationId,
  });

  const formattedData = useMemo(
    (): IZtnaNetworkLogData[] =>
      filteredDataset.filter(({ email }): boolean =>
        email.toLowerCase().includes(search.toLowerCase()),
      ),
    [filteredDataset, search],
  );

  const { Filters, options, removeFilter } = useOrgNetworkLogsFilters(
    networkLogs,
    setBackFilters,
    setFilteredDataset,
  );

  const { table } = useTable({
    columns: tableHeaders,
    data: formattedData,
    id: "org-ztna-network-logs-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
    },
    total: formattedData.length,
  });

  return (
    <Table table={table}>
      <Table.Options>
        <Table.Controls>
          <Filters />
          <div id={"actions-container"}>
            <Can
              do={"integrates_api_resolvers_query_ztna_logs_report_url_resolve"}
            >
              <Buttons
                logType={ZtnaLogType.Network}
                organizationId={organizationId}
              />
            </Can>
          </div>
        </Table.Controls>
      </Table.Options>
      <Table.Caption>
        <AppliedFilters onClose={removeFilter} options={options} />
      </Table.Caption>
      <Table.Content />
      <Table.Footer />
    </Table>
  );
};

export { OrganizationZtnaNetworkLogs };
